import './bootstrap.css';
import './slider.css';
import './/../node_modules/toastr/build/toastr.css';
import './App.css'; // Need to be latest css file because it overrides other
import React, { MouseEvent, useEffect, useState } from 'react';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import toastr from 'toastr';
import { TrackballControls } from './TrackballControls.js';
import { PickHelper } from './PickHelper.js';
import { Metadata } from './classes/metadata';
import { Form } from './components/form';
import { TFunction, useTranslation } from 'react-i18next';
import * as constants from './Constants';
import { displayingDate } from './utils';

enum Arch {
  SUP = 'SUP',
  INF = 'INF',
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const THREE = require('three');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const COLORMAP = require('colormap');

// Put here instead of App to not lose it and clean
const scene = new THREE.Scene();
scene.background = new THREE.Color(constants.blueLight);
let camera: THREE.PerspectiveCamera | null = null;
const initCameraPosition = { x: 0, y: 10, z: 20 };
const initCameraRotation: Record<Arch, { x: number; y: number; z: number }> = {
  INF: { x: 0, y: 0, z: 0 },
  SUP: { x: -Math.PI, y: 0, z: -Math.PI },
};
const initCameraUp = { x: 0, y: 1, z: 0 };
let controls: TrackballControls | null = null;
let pickHelper: PickHelper | null = null;
const pickPosition = { x: 0, y: 0 };
let zFlip = false;
let globalView = 'occlusal';
let initCanvasX = -1;
let initCanvasY = -1;
const hardZRightVestibulOffsets: { [patient: string]: Record<Arch, number> } = {
  '1': { INF: 1000, SUP: 30 },
  '2': { INF: 30, SUP: 1000 },
  '3': { INF: 45, SUP: 30 },
  '4': { INF: 15, SUP: 30 },
};

const resizeRendererToDisplaySize = function (renderer: THREE.Renderer) {
  const canvas = renderer.domElement;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  const needResize = canvas.width !== width || canvas.height !== height;
  if (needResize) {
    renderer.setSize(width, height, false);
  }
  return needResize;
};

const moveCamera = function (boxCenter: THREE.Vector3, hardZRightVestibulOffset: number) {
  if (!camera) {
    console.error('No camera');
    return;
  }
  const prevZ = camera.position.z;
  let relY = camera.position.y - boxCenter.y;
  let relZ = camera.position.z - boxCenter.z;
  let relX = camera.position.x - boxCenter.x;
  switch (globalView) {
    case 'occlusal':
      break;
    case 'leftVestibul':
      camera.position.z = camera.position.x;
      camera.position.x = prevZ;
      break;
    case 'rightVestibul':
      camera.position.z = camera.position.x * (1 + hardZRightVestibulOffset);
      camera.position.x = -prevZ;
      break;
    case 'face':
      relX = zFlip ? -relX : relX;
      relY = zFlip ? -relY : relY;
      relZ = zFlip ? -relZ : relZ;
      camera.position.y = boxCenter.y + relZ;
      camera.position.z = boxCenter.z + relY;
      relZ = camera.position.z - boxCenter.z;
      camera.position.x = boxCenter.x + relZ;
      camera.position.z = boxCenter.z - relX;
      break;
    default:
      console.error(`Invalid view : ${globalView}`);
  }
};

const frameArea = function (
  sizeToFitOnScreen: number,
  boxSize: number,
  boxCenter: THREE.Vector3,
  moveCameraAfterChangingTarget: boolean,
  hardZRightVestibulOffset: number,
) {
  if (!camera) {
    console.error('No camera');
    return;
  }
  const halfSizeToFitOnScreen = sizeToFitOnScreen * 0.5;
  const halfFovY = THREE.MathUtils.degToRad(camera.fov * 0.5);
  const distance = halfSizeToFitOnScreen / Math.tan(halfFovY);
  // compute a unit vector that points in the direction the camera is now
  // in the xz plane from the center of the box
  // NEOVISION MODIFICATION : z = zFactor (+1 or -1) to reverse image
  let zFactor = new THREE.Vector3().subVectors(camera.position, boxCenter).z < 0 ? 1 : -1;
  zFactor = zFlip ? zFactor : -zFactor;
  const direction = new THREE.Vector3().subVectors(camera.position, boxCenter).multiply(new THREE.Vector3(1, 0, zFactor)).normalize();

  // move the camera to a position distance units way from the center
  // in whatever direction the camera was from the center already
  // NEOVISION ADDITION : Add x3.2
  let distanceFactor: number;
  switch (globalView) {
    case 'leftVestibul':
      distanceFactor = 7;
      break;
    case 'rightVestibul':
      distanceFactor = 0;
      break;
    default:
      distanceFactor = 3.2;
      break;
  }
  camera.position.copy(direction.multiplyScalar(distanceFactor * distance).add(boxCenter));

  // pick some near and far values for the frustum that will contain the box.
  camera.near = boxSize / 100;
  camera.far = boxSize * 100;
  // Move camera to have the good view. In case of zoom, it is better to not move camera.
  if (moveCameraAfterChangingTarget || globalView === 'rightVestibul') {
    moveCamera(boxCenter, hardZRightVestibulOffset);
  }
  camera.updateProjectionMatrix();

  // point the camera to look at the center of the box
  camera.lookAt(boxCenter.x, boxCenter.y, boxCenter.z);
};

const data2Geometry = function (data: { vertices: number[][]; vertex_colors: number[][]; faces: number[][] }) {
  const vertices = new Float32Array(constants.dim * data['vertices'].length);
  const vertexColors = new Float32Array(constants.dim * data['vertex_colors'].length);
  if (data['vertices'].length !== data['vertex_colors'].length) {
    console.error(`Invalid vertex sizes : ${data['vertices'].length} vs ${data['vertex_colors'].length}`);
  }
  for (let i = 0; i < data['vertices'].length; i++) {
    for (let j = 0; j < constants.dim; j++) {
      vertices[constants.dim * i + j] = data['vertices'][i][j];
      vertexColors[constants.dim * i + j] = data['vertex_colors'][i][j];
    }
  }
  const faces = [];
  for (let i = 0; i < data['faces'].length; i++) {
    for (let j = 0; j < constants.dim; j++) {
      faces[constants.dim * i + j] = data['faces'][i][j];
    }
  }

  const geometry = new THREE.BufferGeometry() as THREE.BufferGeometry;
  geometry.setIndex(faces);
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, constants.dim));
  geometry.setAttribute('color', new THREE.BufferAttribute(vertexColors, constants.dim));
  geometry.computeVertexNormals();
  return geometry;
};

const createMaterial = function (
  displayType: string,
  shininess: number,
  colors: string[],
  id: string,
  obj: { label: string; geometry: THREE.BufferGeometry },
  i: number,
) {
  let material;
  switch (displayType) {
    case 'scan':
      material = new THREE.MeshPhongMaterial({ shininess, color: constants.white, specular: constants.specular, vertexColors: true });
      break;
    case 'segmentation':
      if (id === 'ging') {
        material = new THREE.MeshPhongMaterial({ shininess, color: constants.white, specular: constants.specular, vertexColors: true });
      } else {
        const color = i % 2 === 0 ? colors[i / 2] : colors[Math.floor(i / 2) + Math.floor(colors.length / 2)];
        material = new THREE.MeshPhongMaterial({ shininess, color, specular: constants.specular });
      }
      break;
    case 'classification':
      if (obj.label.includes('_C')) {
        material = new THREE.MeshPhongMaterial({ shininess, color: 0x545454, specular: constants.specular });
      } else if (obj.label.includes('_T')) {
        material = new THREE.MeshPhongMaterial({ shininess, color: 0xffde59, specular: constants.specular });
      } else if (obj.label.includes('_R')) {
        material = new THREE.MeshPhongMaterial({ shininess, color: 0xd9d9d9, specular: constants.specular });
      } else {
        material = new THREE.MeshPhongMaterial({ shininess, color: 0xffffff, specular: constants.specular, vertexColors: true });
      }
      break;
    default:
      console.error(`Invalid display type ${displayType}`);
  }
  return material;
};

const plotVertexColor = function (
  geometries: { [id: string]: { label: string; geometry: THREE.BufferGeometry } },
  displayType: string,
  gumDisplay: boolean,
) {
  const colors = COLORMAP({ colormap: 'jet', nshades: Object.keys(geometries).length - 1, format: 'hex', alpha: 1 });
  let i = 0;
  for (const [id, obj] of Object.entries(geometries)) {
    const shininess = id === 'ging' ? 0 : constants.gumShininess;
    if (id !== 'ging' || gumDisplay) {
      scene.add(new THREE.Mesh(obj.geometry, createMaterial(displayType, shininess, colors, id, obj, i)));
      scene.add(
        new THREE.Mesh(
          obj.geometry,
          new THREE.MeshPhongMaterial({ shininess, color: constants.blueLight, specular: constants.specular, side: THREE.BackSide }),
        ),
      );
    }
    i++;
  }
};

const updateControls = function (geometry: THREE.BufferGeometry, moveCameraAfterChangingTarget: boolean, hardZRightVestibulOffset = 0) {
  if (!controls) {
    return;
  }
  geometry.computeBoundingSphere();
  if (!geometry.boundingSphere) {
    console.error('Failed to compute bounding sphere');
    return;
  }
  const boxSize = geometry.boundingSphere.radius;
  const boxCenter = geometry.boundingSphere.center;
  frameArea(boxSize * 0.5, boxSize, boxCenter, moveCameraAfterChangingTarget, hardZRightVestibulOffset);
  controls.maxDistance = boxSize * 10;
  controls.target.copy(boxCenter);
  controls.update();
};

const zoomOnPickPosition = function () {
  if (pickHelper && pickHelper.pickedObject) {
    updateControls((pickHelper.pickedObject as THREE.Mesh).geometry, false);
  }
};

const zoomOnTeeth = function (e: MouseEvent, geometries: { [id: string]: { label: string; geometry: THREE.BufferGeometry } }) {
  let target: HTMLElement = e.target as HTMLElement;
  if (target.nodeName === 'IMG') {
    if (!target.parentElement) {
      console.error(`Following image has no parent node : ${target}`);
      return;
    }
    target = target.parentElement;
  }
  if (geometries[parseInt(target.innerText, 10)]) {
    updateControls(geometries[parseInt(target.innerText, 10)].geometry, false);
  }
};

const updateArchOrZoomOnTeeth = function (
  currentArch: Arch,
  newArch: Arch,
  setArch: React.Dispatch<React.SetStateAction<Arch>>,
  e: MouseEvent,
  geometries: Record<Arch, { [id: string]: { label: string; geometry: THREE.BufferGeometry } }>,
) {
  if (currentArch !== newArch) {
    updateArch(newArch, setArch);
  } else {
    zoomOnTeeth(e, geometries[currentArch]);
  }
};

const getCanvasRelativePosition = function (event: MouseEvent, canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) * canvas.width) / rect.width,
    y: ((event.clientY - rect.top) * canvas.height) / rect.height,
  };
};

const setPickPosition = function (event: MouseEvent) {
  const canvas = document.querySelector('#c') as HTMLCanvasElement;
  const pos = getCanvasRelativePosition(event, canvas);
  const relX = pos.x / canvas.width;
  const relY = pos.y / canvas.height;
  if (0 < relX && relX < 1 && 0 < relY && relY < 1) {
    pickPosition.x = relX * 2 - 1;
    pickPosition.y = relY * -2 + 1;
  } else {
    clearPickPosition();
  }
};

const clearPickPosition = function () {
  // unlike the mouse which always has a position  if the user stops touching the screen we want
  // to stop picking. For now we just pick a value unlikely to pick something
  pickPosition.x = -100000;
  pickPosition.y = -100000;
};

const createCamera = function (canvas: HTMLCanvasElement) {
  const fov = 45;
  const aspect = 2;
  const near = 5;
  const far = 100;
  camera = new THREE.PerspectiveCamera(fov, aspect, near, far) as THREE.PerspectiveCamera;
  camera.position.set(0, 10, 20);

  controls = new TrackballControls(camera, canvas);
  controls.target.set(0, 5, 0);
  controls.update();

  pickHelper = new PickHelper(setPickPosition, zoomOnPickPosition, clearPickPosition, false);

  const color = 0xffffff;
  const intensity = 1;
  const light = new THREE.PointLight(color, intensity);
  light.position.set(0, 0, 0);
  camera.add(light);
};

const editTooltip = function (tooltip: HTMLElement) {
  if (pickHelper && pickHelper.pickedObject) {
    tooltip.style.visibility = 'visible';
    const label = (pickHelper.pickedObject as THREE.Mesh).geometry.name;
    if (label[0] === 'g') {
      tooltip.innerHTML = 'Gum';
    } else {
      tooltip.innerHTML = label[0] + label[1];
    }
  } else {
    tooltip.style.visibility = 'hidden';
  }
};

const plotGeometries = function (
  geometries: { [id: string]: { label: string; geometry: THREE.BufferGeometry } },
  displayType: string,
  gumDisplay: boolean,
) {
  if (Object.keys(geometries).length === 0) {
    return;
  }
  console.log('updateGEOplot');
  const canvas = document.querySelector('#c');
  if (canvas && initCanvasX === -1) {
    initCanvasX = canvas.getBoundingClientRect().x;
    initCanvasY = canvas.getBoundingClientRect().y;
  }
  const renderer = new THREE.WebGLRenderer({ canvas });

  if (!camera) {
    createCamera(canvas as HTMLCanvasElement);
  }
  // Put addition to scene here because we clean all the scene at each time
  scene.add(camera);

  plotVertexColor(geometries, displayType, gumDisplay);

  function render(time: number) {
    time *= 0.001; // convert to seconds;
    resizeRendererToDisplaySize(renderer);
    const tooltip = document.querySelector('#tooltip');
    if (pickHelper && tooltip) {
      pickHelper.pick(pickPosition, scene, camera, time);
      editTooltip(tooltip as HTMLElement);
    }
    renderer.render(scene, camera);
    requestAnimationFrame(render);
    if (controls) {
      controls.update();
    }
  }
  requestAnimationFrame(render);
};

const updatePatient = function (
  event: React.ChangeEvent<HTMLSelectElement>,
  setPatient: React.Dispatch<React.SetStateAction<string>>,
  setView: React.Dispatch<React.SetStateAction<string>>,
  startingView: string,
) {
  console.log('updatePatient ' + event.target.value);
  setView(startingView);
  setPatient(event.target.value);
};

const updateDisplayType = function (value: string, setDisplayType: React.Dispatch<React.SetStateAction<string>>) {
  console.log('change display type ' + value);
  setDisplayType(value);
};

const updateArch = function (arch: Arch, setArch: React.Dispatch<React.SetStateAction<Arch>>) {
  console.log('change arch ' + arch);
  zFlip = arch === Arch.INF;
  setArch(arch);
};

const getToothImage = function (i: number) {
  let teethImage: string;
  if (i % 10 < 6) {
    teethImage = 'Small_teeth';
  } else {
    teethImage = 'Large_teeth';
  }
  return teethImage + '.png';
};

const createToothButton = function (
  i: number,
  missingTooth: boolean,
  currentArch: Arch,
  toothArch: Arch,
  setArch: React.Dispatch<React.SetStateAction<Arch>>,
  geometries: Record<Arch, { [id: string]: { label: string; geometry: THREE.BufferGeometry } }>,
) {
  return (
    <button
      key={i}
      type='button'
      disabled={missingTooth}
      className='teeth btn btn-info no-padding'
      onClick={e => updateArchOrZoomOnTeeth(currentArch, toothArch, setArch, e, geometries)}
    >
      <img
        src={getToothImage(i)}
        alt='Teeth image'
        className={`tooth-img ${missingTooth ? 'gray-tooth' : ''} ${toothArch === Arch.SUP ? ' reversed-tooth' : ''}`}
      />
      <br />
      {i}
    </button>
  );
};

const readJsonAsync = function <T>(filename: string) {
  return new Promise<T>(function (resolve, reject) {
    const opaque = document.querySelector('#opaque') as HTMLElement;
    const loader = document.querySelector('#loader') as HTMLElement;
    opaque.style.display = 'block';
    loader.style.display = 'block';
    const filepath = `./data/${filename}.json`;
    const request = new XMLHttpRequest();
    request.open('GET', filepath, true);
    request.send(null);
    request.onreadystatechange = function () {
      if (request.readyState === constants.httpFinalState && request.status === constants.httpOKcode) {
        const type = request.getResponseHeader('Content-Type');
        if (type && type.indexOf('text') !== 0) {
          try {
            const jsonDict = JSON.parse(request.responseText);
            opaque.style.display = 'none';
            loader.style.display = 'none';
            resolve(jsonDict);
          } catch (error) {
            opaque.style.display = 'none';
            loader.style.display = 'none';
            reject(error);
          }
        } else {
          opaque.style.display = 'none';
          loader.style.display = 'none';
          reject(new Error(`Error when loading ${filepath}`));
        }
      } else if (request.readyState === constants.httpFinalState) {
        opaque.style.display = 'none';
        loader.style.display = 'none';
        reject(new Error(`Error when loading ${filepath}`));
      }
    };
  });
};

const jsonToGeometry = function (jsonDict: [{ label: string; vertices: number[][]; vertex_colors: number[][]; faces: number[][] }]) {
  const readGeometries: { [id: string]: { label: string; geometry: THREE.BufferGeometry } } = {};
  for (const elem of jsonDict) {
    const id = elem['label'].split('_')[0];
    const geometry = data2Geometry(elem);
    geometry.name = id;
    readGeometries[id] = { label: elem['label'], geometry };
  }
  return readGeometries;
};

const applyRotationGeometries = function (
  geometriesToRotate: { [id: string]: { label: string; geometry: THREE.BufferGeometry } },
  angleX: number,
  angleY: number,
  angleZ: number,
) {
  for (const obj of Object.values(geometriesToRotate)) {
    if (angleX !== 0) {
      obj.geometry.rotateX(angleX);
    }
    if (angleY !== 0) {
      obj.geometry.rotateY(angleY);
    }
    if (angleZ !== 0) {
      obj.geometry.rotateZ(angleZ);
    }
  }
};

const fillTable = function (
  geometries: Record<Arch, { [id: string]: { label: string; geometry: THREE.BufferGeometry } }>,
  arch: Arch,
  decayRows: JSX.Element[],
  tartarRows: JSX.Element[],
  rootRows: JSX.Element[],
) {
  if (!geometries[arch]) {
    return;
  }
  for (const [id, obj] of Object.entries(geometries[arch])) {
    if (obj.label.includes('_')) {
      const classes = obj.label.split('_')[1];
      if (classes.includes('C')) {
        decayRows.push(
          <div key={'decay-' + id} className='circle decay-color'>
            {id}
          </div>,
        );
      }
      if (classes.includes('T')) {
        tartarRows.push(
          <div key={'tartar-' + id} className='circle tartar-color'>
            {id}
          </div>,
        );
      }
      if (classes.includes('R')) {
        rootRows.push(
          <div key={'root-' + id} className='circle root-color'>
            {id}
          </div>,
        );
      }
    }
  }
};

const scrollFunction = function () {
  const scrollBtn = document.querySelector('#scroll-btn');
  if (!scrollBtn) {
    return;
  }
  // document.body is here for Safari
  const scrollBottom = document.body.scrollTop + document.documentElement.scrollTop + window.innerHeight;
  const isFullBottom = document.body.scrollHeight - scrollBottom === 0;
  if (!isFullBottom) {
    (scrollBtn as HTMLElement).style.display = 'block';
  } else {
    (scrollBtn as HTMLElement).style.display = 'none';
  }
};

const toastError = function (err: string, t: TFunction<'translation'>) {
  toastr.error(err, t('ERROR'), {
    timeOut: constants.toastTimeoutMS,
    positionClass: constants.toastPosition,
  });
};

document.addEventListener('mousemove', e => {
  if (pickHelper && pickHelper.pickedObject) {
    const tooltip = document.querySelector('#tooltip');
    const canvas = document.querySelector('#c') as HTMLCanvasElement;
    const canvasRect = canvas.getBoundingClientRect();
    const x = e.clientX - canvasRect.x + initCanvasX;
    const y = e.clientY - canvasRect.y + initCanvasY;
    (tooltip as HTMLElement).style.left = x + 'px';
    (tooltip as HTMLElement).style.top = y + 'px';
  }
});

window.onscroll = function () {
  scrollFunction();
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
function App() {
  const [patient, setPatient] = useState('3');
  const [arch, setArch] = useState(Arch.SUP);
  const emptyGeometries: Record<Arch, { [id: string]: { label: string; geometry: THREE.BufferGeometry } }> = { SUP: {}, INF: {} };
  const [geometries, setGeometries] = useState(emptyGeometries);
  const emptyMetadata: Metadata = new Metadata();
  const [metadata, setMetadata] = useState(emptyMetadata);
  const emptyTeeth: JSX.Element[] = [];
  const [upperTeeth, setUpperTeeth] = useState(emptyTeeth);
  const [lowerTeeth, setLowerTeeth] = useState(emptyTeeth);
  const [displayType, setDisplayType] = useState('scan');
  const [gumDisplay, setGumDisplay] = useState(true);
  const [view, setView] = useState(globalView);
  const { t, i18n } = useTranslation();
  useEffect(() => {
    console.log('updateGEO and Metadata');
    readJsonAsync<[{ label: string; vertices: number[][]; vertex_colors: number[][]; faces: number[][] }]>(`${patient} INF`)
      .then(infJSON => {
        readJsonAsync<[{ label: string; vertices: number[][]; vertex_colors: number[][]; faces: number[][] }]>(`${patient} SUP`)
          .then(supJSON => {
            const infGeometries = jsonToGeometry(infJSON);
            applyRotationGeometries(infGeometries, 0, 0, Math.PI);
            setGeometries({ INF: infGeometries, SUP: jsonToGeometry(supJSON) });
            readJsonAsync<Metadata>(`${patient} metadata`)
              .then(metadataJSON => {
                metadataJSON['medForm']['latestConsultationDate'] = new Date(metadataJSON['medForm']['latestConsultationDate']);
                setMetadata(metadataJSON);
              })
              .catch(err => {
                toastError(err, t);
              });
          })
          .catch(err => {
            toastError(err, t);
          });
      })
      .catch(err => {
        toastError(err, t);
      });
    return () => {
      if (geometries[Arch.INF]) {
        for (const [, obj] of Object.entries(geometries[Arch.INF])) {
          obj.geometry.dispose();
        }
      }
      if (geometries[Arch.SUP]) {
        for (const [, obj] of Object.entries(geometries[Arch.SUP])) {
          obj.geometry.dispose();
        }
      }
    };
  }, [patient]);
  useEffect(() => {
    if (geometries[arch]) {
      plotGeometries(geometries[arch], displayType, gumDisplay);
    }
    return () => {
      while (scene.children.length > 0) {
        if (scene.children[0].material) {
          scene.children[0].material.dispose();
        }
        scene.remove(scene.children[0]);
      }
    };
  }, [geometries, arch, displayType, gumDisplay]);
  useEffect(() => {
    if (camera && geometries[arch]) {
      console.log('updateCamera');
      camera.position.set(initCameraPosition.x, initCameraPosition.y, initCameraPosition.z);
      camera.rotation.set(initCameraRotation[arch].x, initCameraRotation[arch].y, initCameraRotation[arch].z);
      camera.up.set(initCameraUp.x, initCameraUp.y, initCameraUp.z);
      let hardZRightVestibulOffset = 0;
      try {
        hardZRightVestibulOffset = hardZRightVestibulOffsets[patient][arch];
      } catch (error) {
        console.error(error);
      }
      updateControls(geometries[arch]['ging'].geometry, true, hardZRightVestibulOffset);
    }
  }, [geometries, arch]);
  useEffect(() => {
    if (!geometries[arch]) {
      return;
    }
    console.log('updateTeeth');
    const upperTeethGeo = [];
    for (let i = constants.teethInfRightStop; i >= constants.teethInfRightStart; i--) {
      const missingTooth = !Object.prototype.hasOwnProperty.call(geometries[Arch.SUP], i);
      upperTeethGeo.push(createToothButton(i, missingTooth, arch, Arch.SUP, setArch, geometries));
    }
    for (let i = constants.teethInfLeftStart; i <= constants.teethInfLeftStop; i++) {
      const missingTooth = !Object.prototype.hasOwnProperty.call(geometries[Arch.SUP], i);
      upperTeethGeo.push(createToothButton(i, missingTooth, arch, Arch.SUP, setArch, geometries));
    }
    setUpperTeeth(upperTeethGeo);
    const lowerTeethGeo = [];
    for (let i = constants.teethSupRightStop; i >= constants.teethSupRightStart; i--) {
      const missingTooth = !Object.prototype.hasOwnProperty.call(geometries[Arch.INF], i);
      lowerTeethGeo.push(createToothButton(i, missingTooth, arch, Arch.INF, setArch, geometries));
    }
    for (let i = constants.teethSupLeftStart; i <= constants.teethSupLeftStop; i++) {
      const missingTooth = !Object.prototype.hasOwnProperty.call(geometries[Arch.INF], i);
      lowerTeethGeo.push(createToothButton(i, missingTooth, arch, Arch.INF, setArch, geometries));
    }
    setLowerTeeth(lowerTeethGeo);
  }, [geometries, arch]);
  useEffect(() => {
    let y1Angle = 0;
    let x2Angle = 0;
    let zAngle = 0;
    if (globalView === 'face' || view === 'face') {
      zAngle = Math.PI;
    }
    // Remove previous vectibular rotations
    if (globalView === 'leftVestibul') {
      x2Angle = -Math.PI / 2;
      y1Angle = -Math.PI / 2;
    }
    if (globalView === 'rightVestibul') {
      x2Angle = -Math.PI / 2;
    }
    for (const archGeometries of Object.values(geometries)) {
      if (archGeometries) {
        applyRotationGeometries(archGeometries, 0, y1Angle, 0);
        applyRotationGeometries(archGeometries, x2Angle, 0, 0);
      }
    }
    let x1Angle = 0;
    y1Angle = 0;
    x2Angle = 0;
    let y2Angle = 0;
    // Add new vectibular rotations
    if (view === 'leftVestibul') {
      x1Angle = Math.PI / 2;
      y2Angle = Math.PI / 2;
    }
    if (view === 'rightVestibul') {
      x1Angle = Math.PI / 2;
    }
    if (geometries[Arch.INF]) {
      let yAngle = 0;
      // Remove previous INF face rotation
      if (globalView === 'face') {
        yAngle = -(71 * Math.PI) / 80;
      }
      applyRotationGeometries(geometries[Arch.INF], 0, yAngle, 0);
      // Add INF z angle
      applyRotationGeometries(geometries[Arch.INF], 0, 0, zAngle);
      yAngle = 0;
      // Add INF face rotation
      if (view === 'face') {
        yAngle = (71 * Math.PI) / 80;
      }
      applyRotationGeometries(geometries[Arch.INF], 0, yAngle, 0);
    }
    for (const archGeometries of Object.values(geometries)) {
      if (archGeometries) {
        applyRotationGeometries(archGeometries, x1Angle, y1Angle, 0);
        applyRotationGeometries(archGeometries, x2Angle, y2Angle, 0);
      }
    }
    globalView = view;
    if (geometries[arch] && camera) {
      console.log('updateView ' + globalView);
      camera.position.set(initCameraPosition.x, initCameraPosition.y, initCameraPosition.z);
      camera.rotation.set(initCameraRotation[arch].x, initCameraRotation[arch].y, initCameraRotation[arch].z);
      camera.up.set(initCameraUp.x, initCameraUp.y, initCameraUp.z);
      let hardZRightVestibulOffset = 0;
      try {
        hardZRightVestibulOffset = hardZRightVestibulOffsets[patient][arch];
      } catch (error) {
        console.error(error);
      }
      updateControls(geometries[arch]['ging'].geometry, true, hardZRightVestibulOffset);
    }
  }, [view]);
  const decayRows: JSX.Element[] = [];
  const tartarRows: JSX.Element[] = [];
  const rootRows: JSX.Element[] = [];
  fillTable(geometries, Arch.SUP, decayRows, tartarRows, rootRows);
  fillTable(geometries, Arch.INF, decayRows, tartarRows, rootRows);
  const EHPAD: JSX.Element[] = [];
  if (metadata.EHPADName) {
    EHPAD.push(
      <div>
        <h3>
          {t('EHPAD')} {metadata.EHPADName}
        </h3>
        <p className='metadata'>
          Contact direct
          <br />
          {metadata.EHPADPhone}
        </p>
      </div>,
    );
  }
  const IDE: JSX.Element[] = [];
  if (metadata.IDEName) {
    IDE.push(
      <React.Fragment>
        {metadata.IDEName}
        <br />
        N°ADELI {metadata.IDEADELI}
        <br />
      </React.Fragment>,
    );
  }
  return (
    <div className='App'>
      <div className='container-fluid all-page no-padding'>
        <div className='row flex-container gray-background'>
          <div className='col-3'>
            <img src='LOVIS.png' alt='Lovis Care logo' width='250px' />
          </div>
          <div className='col-9 center-vertical'>
            <div className='right-alignment-bis'>
              Dr. DENTIST
              <img src='profil.jpeg' alt='Go to profil' className='ml-3 mb-2' />
              <img src='log_out.jpeg' alt='Log out' className='mb-2 mr-5' />
            </div>
          </div>
        </div>
        <div className='row white-background'>
          <div className='mx-auto reduce-font-size-80p'>
            <label className='mr-3'>{t('Patient')}:</label>
            <select className='align-middle' value={patient} onChange={event => updatePatient(event, setPatient, setView, 'occlusal')}>
              <option value='1'>M. Michel ODENTS</option>
              <option value='2'>Mme Clara LOVISODF</option>
              <option value='3'>Mme Alice UNEDENT</option>
              <option value='4'>M. John DOE</option>
            </select>
          </div>
        </div>

        <div className='row mb-5 mt-3'>
          <h1 className='mx-auto title'>{t('Diagnostic and Visualization')}</h1>
        </div>
        <div className='row'>
          <div className='col-6'>
            <div className='row mb-5 menu-min-height'>
              <div className='col-4 metadata-container'>
                <div>
                  <div className='menu-back ml-5'>{t('Orientation')}</div>
                  <ToggleButtonGroup
                    className='ml-5'
                    name='value'
                    type='radio'
                    value={view}
                    vertical={true}
                    onChange={value => setView(value)}
                  >
                    <ToggleButton value={'occlusal'} variant='outline-primary'>
                      {t('Occlusal')}
                    </ToggleButton>
                    <ToggleButton value={'face'} variant='outline-primary'>
                      {t('Face')}
                    </ToggleButton>
                    <ToggleButton value={'rightVestibul'} variant='outline-primary'>
                      {t('Right Vesti')}
                    </ToggleButton>
                    <ToggleButton value={'leftVestibul'} variant='outline-primary'>
                      {t('Left Vesti')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
                <div>
                  <div className='menu-back ml-5'>{t('Displaying')}</div>
                  <ToggleButtonGroup
                    className='ml-5'
                    name='value'
                    type='radio'
                    value={displayType}
                    vertical={true}
                    onChange={value => updateDisplayType(value, setDisplayType)}
                  >
                    <ToggleButton value={'scan'} variant='outline-primary'>
                      {t('Scan')}
                    </ToggleButton>
                    <ToggleButton value={'segmentation'} variant='outline-primary'>
                      {t('Segmentation')}
                    </ToggleButton>
                    <ToggleButton value={'classification'} variant='outline-primary'>
                      {t('Classification')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
                <div className='menu-back ml-5 center-vertical-items'>
                  <label className='menu-text'>{t('Display Gum')}</label>
                  <label className='switch align-middle right-alignment'>
                    <input type='checkbox' defaultChecked={gumDisplay} onChange={() => setGumDisplay(!gumDisplay)}></input>
                    <span className='slider' />
                  </label>
                </div>
              </div>
              <div className='col-8 canvas-centering no-padding'>
                <div></div>
                <div className='canvas-div'>
                  <canvas id='c' />
                </div>
                <div></div>
              </div>
            </div>
          </div>
          <div className='col-6'>
            <div className='row mt-2 flex-container teeth-max-width'>
              <button disabled={arch === Arch.SUP} className='btn arch-btn mr-5' onClick={() => updateArch(Arch.SUP, setArch)}>
                {t('Upper')}
                {arch === Arch.INF ? '' : <img src='eye.png' alt='Current arch' height='15px' className='invert-color'></img>}
              </button>
              {upperTeeth}
            </div>
            <div className='row mt-2 mb-5 flex-container teeth-max-width'>
              <button disabled={arch === Arch.INF} className='btn arch-btn mr-5' onClick={() => updateArch(Arch.INF, setArch)}>
                {t('Lower')}
                {arch === Arch.SUP ? '' : <img src='eye.png' alt='Current arch' height='15px' className='invert-color'></img>}
              </button>
              {lowerTeeth}
            </div>
            <div className='row flex-container class-max-width mb-2'>
              <div className='col-4'>
                <div
                  className='class-name'
                  onClick={() => {
                    setDisplayType('classification');
                  }}
                >
                  {t('Tartar')}
                </div>
                <div className='class-values'>{tartarRows}</div>
              </div>
              <div className='col-4'>
                <div
                  className='class-name'
                  onClick={() => {
                    setDisplayType('classification');
                  }}
                >
                  {t('Decay')}
                </div>
                <div className='class-values'>{decayRows}</div>
              </div>
              <div className='col-4'>
                <div
                  className='class-name'
                  onClick={() => {
                    setDisplayType('classification');
                  }}
                >
                  {t('Root')}
                </div>
                <div className='class-values'>{rootRows}</div>
              </div>
            </div>
          </div>
        </div>
        <div className='row white-background'>
          <div className='col-1'></div>
          <div className='col-10'>
            <h1 className='mx-auto title title-color-dark mt-3'>{t('Report')}</h1>
          </div>
          <div className='col-1 see-below-container'>
            <button
              id='scroll-btn'
              className='btn btn-dark see-below'
              onClick={() => {
                window.scrollTo(0, document.body.scrollHeight);
              }}
            >
              {t('See')} <span className='font-weight-bold see-below ml-2'>ᐯ</span>
            </button>
          </div>
        </div>
        <div className='row white-background'>
          <div className='col-3 metadata-background metadata-container ml-5 mb-5'>
            <div className='mt-3'>
              <h1 className='metadata'>{t('Reminder')}</h1>
            </div>
            {EHPAD}
            <div>
              <h3>{metadata.IDEName ? t('IDE') : t('Collect')}</h3>
              <p className='metadata'>
                {IDE}
                {t('Collection date')}: {displayingDate(new Date(metadata.collectionDate), i18n.language, true)}
              </p>
            </div>
            <div>
              <h3 className='line-break'>{t('Dentist')}</h3>
              <p className='metadata'>
                {metadata.dentistName}
                <br />
                N°RPPS {metadata.dentistRPPS}
                <br />
                {t('Analysis date')}: {displayingDate(new Date(metadata.analysisDate), i18n.language, true)}
              </p>
            </div>
          </div>
          <div className='col-1'></div>
          <div className='col-7 move-50px-left'>
            <div className='row mt-5'>
              <div className='col-3'>
                <p className='report-title'>{t('Reminder')}</p>
              </div>
              <div className='col-9'>
                <p className='report' contentEditable={true} suppressContentEditableWarning={true}>
                  {metadata.reminder}
                </p>
              </div>
            </div>
            <div className='row'>
              <div className='col-3'>
                <p className='report-title'>{t('Pre-diagnostic')}</p>
              </div>
              <div className='col-9'>
                <p className='report' contentEditable={true} suppressContentEditableWarning={true}>
                  {metadata.preDiagnostic}
                </p>
              </div>
            </div>
            <div className='row'>
              <div className='col-3'>
                <p className='report-title'>{t('Point of Interest')}</p>
              </div>
              <div className='col-9'>
                <p className='report' contentEditable={true} suppressContentEditableWarning={true}>
                  {metadata.PoI}
                </p>
              </div>
            </div>
            <div className='row'>
              <div className='col-3'>
                <p className='report-title'>{t('Remarks')}</p>
              </div>
              <div className='col-9'>
                <p className='report' contentEditable={true} suppressContentEditableWarning={true}>
                  {metadata.remarks}
                </p>
              </div>
            </div>
            <div className='row right-alignment-bis mt-3 mb-3'>
              <button type='button' className='btn dark-btn ml-3' data-toggle='modal' data-target='#exampleModal'>
                {t('Medical Form')}
              </button>
              <button type='button' className='btn dark-btn ml-3'>
                {t('Download')} <img src='pdf.png' alt='Download report as pdf' width='40px' height='40px' />
              </button>
            </div>
          </div>
          <div className='col-1'></div>
        </div>
      </div>
      <span id='tooltip' className='tooltiptext'>
        42
      </span>
      <div id='opaque'></div>
      <div id='loader'></div>
      <div className='modal fade' id='exampleModal' aria-labelledby='exampleModalLabel' aria-hidden='true'>
        <div className='modal-dialog' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                {t('Medical Form for')} {metadata.name}
              </h5>
              <button type='button' className='close' data-dismiss='modal' aria-label='Close'>
                <span aria-hidden='true'>&times;</span>
              </button>
            </div>
            <div className='modal-body'>
              <Form medForm={metadata['medForm']} />
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn close-btn' data-dismiss='modal'>
                {t('Close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
